import React from 'react';
import { Center, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useLocation } from 'react-router-dom';
import {
  isPermissionExemptPath,
  usePermissionContext,
  useResourcePermissionsReady,
} from '../../permissions/scheduler';
import { useResourcePermissionsStore } from '../../stores/zustand/resourcePermissionsStore';

function PermissionLoader() {
  return (
    <Center style={{ flex: 1, minHeight: 280, height: '100%' }}>
      <Loader size="md" />
    </Center>
  );
}

export function SchedulerAccessDenied() {
  return (
    <Center style={{ flex: 1, minHeight: 280, height: '100%', padding: 24 }}>
      <Stack align="center" gap="md" maw={480}>
        <ThemeIcon size={56} radius="xl" variant="light" color="gray">
          <IconLock size={28} />
        </ThemeIcon>
        <Text size="lg" fw={600} ta="center">
          You don&apos;t have permission to view this page
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          Contact your administrator if you believe you should have access.
        </Text>
      </Stack>
    </Center>
  );
}

/**
 * Route-level gate for scheduler pages. Fail-closed until permissions are ready.
 * Request tracker is exempt from permission checks.
 */
const SchedulerPermissionGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const permissionContext = usePermissionContext();
  const { status } = useResourcePermissionsReady();
  const permissionExempt = isPermissionExemptPath(location.pathname);
  const hasAccess = useResourcePermissionsStore((s) =>
    s.hasModuleAccess(permissionContext)
  );
  const canViewPath = useResourcePermissionsStore((s) =>
    s.canAccessPath(location.pathname, permissionContext)
  );

  if (permissionExempt) {
    return <>{children}</>;
  }

  if (status === 'idle' || status === 'loading') {
    return <PermissionLoader />;
  }

  if (status === 'error') {
    return <SchedulerAccessDenied />;
  }

  if (!hasAccess) {
    return <SchedulerAccessDenied />;
  }

  if (!canViewPath) {
    return <SchedulerAccessDenied />;
  }

  return <>{children}</>;
};

export default SchedulerPermissionGate;
